import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { isIncludeFields, sortByAlphabet } from 'utils/array'
import { UserWithFG, UsersGlobalFilter } from '../types'
import { fromNow } from 'utils/date'
import { Row } from 'react-table'
import Filter, { FilterOption } from 'components/organisms/AdvancedFilter'
import Accordion from 'components/molecules/Accordion'
import { parseInfoField } from 'utils/parseInfoField'
import { useQuery } from 'react-query'
import { getContactsStatsFn, getDevicesStatsFn, getLastLoginStatsFn } from '../../../api'

export const AdvancedFilters: FC<{
  globalFilter: UsersGlobalFilter
  i_realm: number
  setGlobalFilter: (filterValue: any) => void
}> = ({ globalFilter, setGlobalFilter, i_realm }) => {
  const [t] = useTranslation()
  const { data: lastLogins } = useQuery(['lastLogins', i_realm], getLastLoginStatsFn, {
    enabled: !!i_realm, //&& Object.values(featureGroups || {}).length === 0
    select: (data) => data.last_login,
  })

  const { data: devicesStats } = useQuery(['devicesStats', i_realm], getDevicesStatsFn, {
    enabled: !!i_realm, //&& Object.values(featureGroups || {}).length === 0
  })

  const { data: contactsStats } = useQuery(['contactsStats', i_realm], getContactsStatsFn, {
    select: (data) => ({
      ...data,
      info: {
        role: (data.info.role || []).sort((a, b) => sortByAlphabet(a.value?.toString(), b.value?.toString())),
        cabinet: (data.info.cabinet || []).sort((a, b) => sortByAlphabet(a.value?.toString(), b.value?.toString())),
      },
    }),
    ///keepPreviousData: false,
  })

  const handleChangeAdvancedFilter = ({
    OS,
    version,
    lastLogin,
    cabinet,
    role,
  }: {
    OS?: string | null
    version?: string | null
    lastLogin?: string | null
    cabinet?: string | null
    role?: string | null
  }) => {
    setGlobalFilter({
      ...globalFilter,
      OS: OS ? (OS === globalFilter.OS ? '' : OS) : globalFilter.OS,
      version: version ? (version === globalFilter.version ? '' : version) : globalFilter.version,
      lastLogin: lastLogin ? (lastLogin === globalFilter.lastLogin ? '' : lastLogin) : globalFilter.lastLogin,
      cabinet: cabinet ? (cabinet === globalFilter.cabinet ? '' : cabinet) : globalFilter.cabinet,
      role: role ? (role === globalFilter.role ? '' : role) : globalFilter.role,
    })
  }

  return (
    <Filter>
      <Accordion
        title={t('common.os')}
        items={(devicesStats?.devices || []).map((os) => (
          <FilterOption
            key={os.deviceos}
            filterName={os.deviceos}
            count={os.count}
            //isGroup={os === 'macOS' || OS === 'Windows' || OS === 'iOS' || OS === 'Android' || OS === 'iPadOS'}
            isSelected={globalFilter.OS === os.deviceos}
            onClick={() => handleChangeAdvancedFilter({ OS: os.deviceos })}
          />
        ))}
      />
      <Accordion
        title={t('common.version')}
        items={(devicesStats?.client_versions || []).map((version) => (
          <FilterOption
            key={version.client_version}
            filterName={version.client_version}
            count={version.count}
            isSelected={globalFilter.version === version.client_version}
            onClick={() => handleChangeAdvancedFilter({ version: version.client_version })}
          />
        ))}
      />
      <Accordion
        title={t('common.cabinet')}
        items={(contactsStats?.info?.cabinet || []).map((cabinet) => (
          <FilterOption
            key={cabinet.value}
            filterName={cabinet.value}
            count={cabinet.count}
            isSelected={globalFilter.cabinet === cabinet.value}
            onClick={() => handleChangeAdvancedFilter({ cabinet: cabinet.value })}
          />
        ))}
      />
      <Accordion
        title={t('common.role')}
        items={(contactsStats?.info?.role || []).map((role) => (
          <FilterOption
            key={role.value}
            filterName={role.value}
            count={role.count}
            isSelected={globalFilter.role === role.value}
            onClick={() => handleChangeAdvancedFilter({ role: role.value })}
          />
        ))}
      />
      <Accordion
        title={t('common.lastLogin')}
        items={(lastLogins || []).map((lastLogin) => (
          <FilterOption
            key={lastLogin.period + 'period'}
            filterName={t('filter.monthsCount', { count: Number(lastLogin.period) })}
            count={lastLogin.count}
            isSelected={globalFilter.lastLogin === lastLogin.period}
            onClick={() => handleChangeAdvancedFilter({ lastLogin: lastLogin.period })}
          />
        ))}
      />
    </Filter>
  )
}

export const usersGlobalFilter = (
  rows: Row<UserWithFG>[],
  _columnIds: string[],
  filterValue: UsersGlobalFilter
): Row<UserWithFG>[] => {
  let remainingRows = [...rows]

  remainingRows = remainingRows.filter((r) => {
    const { name, last_login } = r.original
    const isActive = Boolean(last_login)
    const isTestUser = name.toLocaleLowerCase().includes('test')

    if (filterValue.status === 'active') {
      return isActive && !isTestUser
    }
    if (filterValue.status === 'pending') {
      return !isActive && !isTestUser
    }
    if (filterValue.status === 'test') {
      return isTestUser
    }

    return !isTestUser
  })

  if (filterValue.search) {
    const escapedValue = filterValue.search.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&')
    const regExp = new RegExp(`${escapedValue}`, 'i')

    remainingRows = remainingRows.filter((r) => {
      const { name } = r.original

      if (isIncludeFields([name], regExp)) {
        return true
      }
      return false
    })
  }

  if (filterValue.OS) {
    remainingRows = remainingRows.filter((r) => {
      const { devices } = r.original

      if (devices.some(({ deviceos }) => deviceos?.includes(filterValue.OS))) {
        return true
      }
      return false
    })
  }

  if (filterValue.version) {
    remainingRows = remainingRows.filter((r) => {
      const { devices } = r.original

      if (devices.some(({ clientversion }) => clientversion === filterValue.version)) {
        return true
      }
      return false
    })
  }

  if (filterValue.cabinet) {
    remainingRows = remainingRows.filter((r) => {
      const { roster } = r.original
      if (!roster) return false

      const { cabinet } = parseInfoField(roster.info)
      return cabinet === filterValue.cabinet
    })
  }

  if (filterValue.role) {
    remainingRows = remainingRows.filter((r) => {
      const { roster } = r.original
      if (!roster) return false

      const { role } = parseInfoField(roster.info)
      return role === filterValue.role
    })
  }

  if (filterValue.lastLogin) {
    remainingRows = remainingRows.filter((r) => {
      const { last_login } = r.original

      if (fromNow(last_login) === filterValue.lastLogin) {
        return true
      }
      return false
    })
  }

  return remainingRows
}
